import { type StandardNavData } from '@/features/cms/components/CMS/types'
import styled from 'styled-components'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { staticColors } from '@/features/shared/theme/staticColors'
import { Headline } from '@shipt/design-system-typography'
import {
  parseCategoryId,
  parseCategoryIds,
  parseCategoryName,
} from '@/features/cms/components/CMS/utils/parse'
import { CMSShelfLayoutContainerConditional } from '@/features/cms/components/CMS/components/Shelves/CMSShelfLayoutContainer'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { screenSizes } from '@shipt/design-system-themes'
import { Image as NextImage } from '@/features/shared/elements/Image/NextImage'
import { useCMSLayoutContext } from '@/features/cms/components/CMS/CMSLayoutContext'
import { StandardNavItem } from '@/features/cms/components/CMS/components/Shelves/StandardNavItem'
import { Shelf } from '@/features/shop/components/Shelf'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { StandardNavRedesign } from '@/features/cms/components/CMS/components/BrandAmplification/StandardNavRedesign'

/**
 * This component corresponds to the `standard_nav` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/standard_nav
 * - **Production** - https://cms.shipt.com/content-type/pool/standard_nav
 */
export const StandardNav = ({
  content_type_id,
  id,
  data: { heading, nav_links, heading_orientation, theme, size },
  name,
  analytic_slug,
  index: shelfIndex,
}: StandardNavData & { index: number }) => {
  const { trackCMSElementViewed, trackCMSElementClicked } = useCMSTrackingEvents()
  const { GLOBAL_HOMEPAGE } = useIsPage()
  const layout = useCMSLayoutContext()
  const displaySublocation = GLOBAL_HOMEPAGE ? (analytic_slug ?? name) : name
  const { screenSm } = useMediaQuery()

  const getPortholes = (isCarousel = false) =>
    nav_links.map(({ data: navItem }, index) => {
      return (
        <StandardNavItem
          isCarousel={isCarousel}
          key={navItem.heading}
          navItem={navItem}
          displaySublocation={displaySublocation}
          onClick={() => {
            const eventProperties = navItem.available_stores
              ? {
                  type: 'button',
                }
              : {
                  shelf_index: shelfIndex,
                  next_location: navItem.link.url,
                  category_id:
                    parseCategoryId(navItem.link.url) ?? parseCategoryIds(navItem.link.url),
                  category_name: parseCategoryName(navItem.link.url),
                }

            trackCMSElementClicked({
              display_format: DISPLAY_FORMAT.HORIZONTAL,
              shelf_name: heading,
              display_sublocation: displaySublocation,
              list_index: index,
              content: navItem.heading,
              ...eventProperties,
            })
          }}
        >
          <SkeletonImage>
            <PortholeImageWrapper portholeCount={nav_links.length}>
              <NextImage src={navItem.image.src} fallbackType="product" alt="" sizes="128px" fill />
            </PortholeImageWrapper>
          </SkeletonImage>
          {!!navItem.heading && (
            <SkeletonConsumer width="8rem" height="1.5rem">
              <LinkName size="md">{navItem.heading}</LinkName>
            </SkeletonConsumer>
          )}
        </StandardNavItem>
      )
    })

  return theme === 'astro-dls' ? (
    <StandardNavRedesign
      id={id}
      index={shelfIndex}
      content_type_id={content_type_id}
      heading={heading}
      nav_links={nav_links}
      displaySublocation={displaySublocation}
      size={size}
    />
  ) : (
    <CMSShelfLayoutContainerConditional
      {...getCMSWrapperProps({ content_type_id, id })}
      layout={layout}
    >
      <ScrollIntoView
        onInView={() => {
          trackCMSElementViewed({
            shelf_index: shelfIndex,
            shelf_name: heading,
            display_sublocation: displaySublocation,
          })
        }}
      >
        {!!heading && (
          <SkeletonHeading>
            <Heading as="h2" textAlign={heading_orientation}>
              {heading}
            </Heading>
          </SkeletonHeading>
        )}
        {nav_links.length > 6 ? (
          <ShelfWrapper hideTitle shelfIndex={shelfIndex} slideSpacing={screenSm ? 18 : 36}>
            {getPortholes(true)}
          </ShelfWrapper>
        ) : (
          <PortholesWrapper portholeCount={nav_links.length} textAlign={heading_orientation}>
            {getPortholes()}
          </PortholesWrapper>
        )}
      </ScrollIntoView>
    </CMSShelfLayoutContainerConditional>
  )
}

const Heading = styled(Headline)<{ textAlign?: 'left' | 'center' }>`
  color: ${staticColors.gray900};
  margin-bottom: 0.5rem;
  margin-top: 1rem;
  text-align: ${({ textAlign }) => textAlign ?? 'center'};

  @media ${screenSizes.tablet} {
    margin-bottom: 1rem;
  }
`

const LinkName = styled(Headline)`
  color: ${staticColors.gray600};
  text-align: center;
`

const PortholesWrapper = styled.div<{
  portholeCount: number
  textAlign: StandardNavData['data']['heading_orientation']
}>`
  white-space: nowrap;
  overflow-x: auto;
  padding-top: 0.5rem;
  margin-bottom: 2.5rem;

  @media ${screenSizes.lgMobile} {
    display: flex;
    overflow: visible;
    justify-content: ${({ portholeCount, textAlign }) =>
      portholeCount <= 3 && textAlign !== 'left' ? 'space-around' : 'space-between'};
  }
`

const SkeletonImage = styled(SkeletonConsumer)`
  border-radius: 100%;
  margin-bottom: 0.5rem;
  width: 4rem;
  height: 4rem;
  margin-right: auto;
  margin-left: auto;
  position: relative;

  @media ${screenSizes.tablet} {
    width: 6rem;
    height: 6rem;
    margin-bottom: 1rem;
  }

  @media ${screenSizes.smDesktop} {
    width: 8rem;
    height: 8rem;
  }
`

const PortholeImageWrapper = styled.div<{
  portholeCount: number
}>`
  overflow: hidden;
  position: relative;
  width: 4rem;
  height: 4rem;
  margin-bottom: 0.5rem;

  @media ${screenSizes.tablet} {
    width: ${({ portholeCount }) => (portholeCount > 6 ? '8rem' : '6rem')};
    height: ${({ portholeCount }) => (portholeCount > 6 ? '8rem' : '6rem')};
    margin-bottom: 1rem;
  }

  @media ${screenSizes.smDesktop} {
    width: 8rem;
    height: 8rem;
  }

  border-radius: 50%;
  ${getBoxShadow('lift', 'light')}
`
const ShelfWrapper = styled(Shelf)`
  & .NavButton-left,
  .NavButton-right {
    margin-bottom: 1rem;
  }

  @media ${screenSizes.tablet} {
    margin-bottom: 2.5rem;
  }
`

const SkeletonHeading = styled(SkeletonConsumer)`
  height: 2rem;
  width: 18.75rem;
  margin-bottom: 1rem;
  margin-right: auto;
  margin-left: auto;
`
